import {
  Container,
  Graphics,
  Rectangle,
  Sprite,
  Text,
  type Texture,
} from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';

/**
 * Persistent 3-slot loadout strip for the multiplayer Reel Race.
 * Rendered inside the slot HUD as a sibling of the BET / SPIN /
 * INFO row so the chrome (gold border + dark navy fill) matches the
 * gear/info cluster pill exactly and the layout scales with
 * {@code hudScale}. Producer brief 2026-05-14: "Cleaner long term."
 *
 * <p>Generic by design — the host (wheel-game React) passes asset
 * aliases (e.g. {@code "item:BOOST"}) for filled slots and the
 * pending-claim badge. The engine widget resolves them via
 * {@link AssetLoader#getTexture} and renders sprites; if a texture
 * is unavailable the slot reads as empty (dashed outline).
 *
 * <p>Visibility, slot contents, and pending state are independent
 * setters so the host can update each without re-creating the
 * widget. {@link onTileClick} is a public callback the host wires
 * to its CLAIM_ITEM dispatcher.
 */
export class LoadoutStripHud extends Container {
  // Tunables (in canvas-internal pixels at hudScale=1, layout
  // multiplies by scale at runtime).
  /** Edge length of one slot tile. */
  static readonly TILE_SIZE = 56;
  /** Gap between adjacent tiles. */
  static readonly TILE_GAP = 8;
  /** Inner pill padding (from pill edge to outer tile edge). */
  static readonly INNER_PAD_X = 10;
  static readonly INNER_PAD_Y = 8;
  /** Number of slots — fixed at 3 per the multiplayer design. */
  static readonly SLOTS = 3;

  /** Total pill width at scale=1. */
  static get pillWidth(): number {
    return (
      LoadoutStripHud.SLOTS * LoadoutStripHud.TILE_SIZE +
      (LoadoutStripHud.SLOTS - 1) * LoadoutStripHud.TILE_GAP +
      LoadoutStripHud.INNER_PAD_X * 2
    );
  }
  static get pillHeight(): number {
    return LoadoutStripHud.TILE_SIZE + LoadoutStripHud.INNER_PAD_Y * 2;
  }

  private readonly background: Graphics;
  private readonly tileBackgrounds: Graphics[] = [];
  private readonly tileIcons: Array<Sprite | null> = [];
  private readonly tileButtons: Container[] = [];
  private readonly pendingBadge: Container;
  private readonly pendingBadgeBg: Graphics;
  private readonly pendingBadgeText: Text;

  private slotAliases: Array<string | null> = [null, null, null];
  private pendingAlias: string | null = null;
  private pendingItemName: string = '';
  private hiddenState = false;
  private pulseTime = 0;
  /** Wall-clock ms of last pendingBadge bob frame — drives the
   *  attention-grab bounce when a claim is pending. */
  private bobTime = 0;

  /** Host-supplied tile-click handler. Fires with the 0-based slot
   *  index. Only fires when a pending claim is showing (otherwise
   *  taps are ignored — there's nothing to swap). */
  onTileClick: ((slotIndex: number) => void) | null = null;

  constructor() {
    super();
    this.eventMode = 'static';

    this.background = new Graphics();
    this.addChild(this.background);

    for (let i = 0; i < LoadoutStripHud.SLOTS; i++) {
      const tile = new Container();
      tile.eventMode = 'static';
      tile.cursor = 'default';
      const tileBg = new Graphics();
      tile.addChild(tileBg);
      this.tileBackgrounds.push(tileBg);
      this.tileIcons.push(null);
      const idx = i;
      tile.on('pointertap', () => {
        if (this.pendingAlias && this.onTileClick) {
          this.onTileClick(idx);
        }
      });
      // Hit area set in layout() once sizing is finalised.
      this.addChild(tile);
      this.tileButtons.push(tile);
    }

    this.pendingBadge = new Container();
    this.pendingBadgeBg = new Graphics();
    this.pendingBadge.addChild(this.pendingBadgeBg);
    this.pendingBadgeText = new Text({
      text: '',
      style: {
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: 14,
        fontWeight: '800',
        fill: 0x1a1304,
        letterSpacing: 1.1,
      },
    });
    this.pendingBadgeText.anchor.set(0.5);
    this.pendingBadge.addChild(this.pendingBadgeText);
    this.pendingBadge.visible = false;
    this.addChild(this.pendingBadge);

    this.drawBackground();
    this.drawTiles();
  }

  // -------- public API --------

  /** Replace the displayed slot contents. Each entry is an asset
   *  alias (from the theme's uiIcons bundle) or null for an empty
   *  slot. The array length must be {@link LoadoutStripHud.SLOTS}. */
  setSlots(aliases: ReadonlyArray<string | null>): void {
    const arr = aliases.slice(0, LoadoutStripHud.SLOTS);
    while (arr.length < LoadoutStripHud.SLOTS) arr.push(null);
    let changed = false;
    for (let i = 0; i < LoadoutStripHud.SLOTS; i++) {
      if (this.slotAliases[i] !== arr[i]) {
        this.slotAliases[i] = arr[i] ?? null;
        changed = true;
      }
    }
    if (changed) this.refreshTileIcons();
  }

  /** Set the pending-claim state. {@code alias} is the incoming
   *  item's icon (null to clear); {@code label} is the player-facing
   *  name shown in the "Tap to replace <Name>" badge below the
   *  strip. When non-null, all 3 tiles pulse + become tappable. */
  setPending(alias: string | null, label: string = ''): void {
    if (this.pendingAlias === alias && this.pendingItemName === label) return;
    this.pendingAlias = alias;
    this.pendingItemName = label;
    this.refreshTiles();
    this.refreshPendingBadge();
  }

  /** Hide the strip with a fade. Slot scene calls this when the
   *  local player presses SPIN — the strip disappears for the reel
   *  motion and reappears when the spin settles. */
  setHidden(hidden: boolean): void {
    if (this.hiddenState === hidden) return;
    this.hiddenState = hidden;
  }

  /** Advance internal animations (pulse + bob + fade). Call once per
   *  frame from the host scene's ticker. Producer brief 2026-05-14:
   *  the squares STAY visible when the player spins — only the
   *  "tap to replace" badge fades. So {@link hiddenState} drives
   *  {@code pendingBadge.alpha} only, not the strip container's
   *  alpha. */
  tick(deltaMs: number): void {
    this.pulseTime = (this.pulseTime + deltaMs) % 1000;
    this.bobTime = (this.bobTime + deltaMs) % 1200;
    // Pending badge fade — visible iff a claim is offered AND the
    // strip isn't in the hidden (mid-spin) state.
    const badgeTargetAlpha =
      this.hiddenState || !this.pendingAlias ? 0 : 1;
    if (Math.abs(this.pendingBadge.alpha - badgeTargetAlpha) > 0.001) {
      const step =
        (deltaMs / 220) *
        (badgeTargetAlpha > this.pendingBadge.alpha ? 1 : -1);
      this.pendingBadge.alpha = Math.max(
        0,
        Math.min(1, this.pendingBadge.alpha + step),
      );
    }
    // Pulse the tile borders only while a claim is actively
    // offered AND visible — pulsing during the spin hide is
    // distracting.
    if (this.pendingAlias && !this.hiddenState) {
      const pulsePhase = (Math.sin((this.pulseTime / 900) * Math.PI * 2) + 1) / 2;
      this.redrawTileBorders(pulsePhase);
    }
    // Bob the pending badge while it's at least partly visible.
    if (this.pendingBadge.alpha > 0.01) {
      const bobPhase = (Math.sin((this.bobTime / 1100) * Math.PI * 2) + 1) / 2;
      this.pendingBadge.y = this.pendingBadgeRestY + 3 * bobPhase;
    }
    // Tile-tap interactivity follows the badge — no clicks while
    // the badge is hidden (mid-spin or no pending claim).
    const tileInteractive = !!this.pendingAlias && !this.hiddenState;
    for (const t of this.tileButtons) {
      t.eventMode = tileInteractive ? 'static' : 'none';
      t.cursor = tileInteractive ? 'pointer' : 'default';
    }
  }

  /** Snap the pending badge to its current target with no fade. */
  snapAlphaToTarget(): void {
    this.pendingBadge.alpha =
      this.hiddenState || !this.pendingAlias ? 0 : 1;
  }

  /** Position the strip in the canvas-internal pixel coordinate
   *  system, matching the slot HUD layout. The strip's right edge
   *  sits {@code GAP} px to the LEFT of the gear/info cluster
   *  pill's left edge. Caller passes the HUD scale + the cluster's
   *  computed pillWidth so the offset stays correct on every
   *  viewport. */
  layoutAt(opts: {
    rightAnchor: number;
    spinButtonY: number;
    clusterPillWidth: number;
    clusterRowEdgeInset: number;
    hudScale: number;
    /** Gap between strip's right edge and cluster pill's left edge. */
    gapBeforeCluster?: number;
  }): void {
    const gap = (opts.gapBeforeCluster ?? 12) * opts.hudScale;
    const scaledPillW = LoadoutStripHud.pillWidth * opts.hudScale;
    const clusterLeftEdge =
      opts.rightAnchor -
      opts.clusterRowEdgeInset * opts.hudScale -
      opts.clusterPillWidth;
    // Anchor is the strip's CENTRE (origin (0,0) is at the pill
    // centre, matching how SlotHudComponent positions its other
    // pill children).
    this.scale.set(opts.hudScale);
    this.x = clusterLeftEdge - gap - scaledPillW / 2;
    this.y = opts.spinButtonY;
    this.layoutChildren();
  }

  /** Position the strip in the sky band ABOVE the slot frame, right-
   *  aligned — the multiplayer Reel Race "special items" HUD per
   *  producer 2026-05-29 ("move the three slots out, top-right above
   *  the reels"). Sits clear of the centred countdown + the top-right
   *  wallet pill. Origin is the strip's centre (matching
   *  {@link layoutAt}). */
  layoutTopRight(opts: {
    /** Slot frame's right edge (canvas px). */
    rightAnchor: number;
    /** Slot frame's top edge (canvas px) — the strip floats just
     *  above this so it reads as "above the reels". */
    frameTopY: number;
    /** Inset from the frame's right edge to the strip's right edge. */
    edgeInset: number;
    hudScale: number;
  }): void {
    const scaledHalfW = (LoadoutStripHud.pillWidth / 2) * opts.hudScale;
    const scaledHalfH = (LoadoutStripHud.pillHeight / 2) * opts.hudScale;
    this.scale.set(opts.hudScale);
    this.x = opts.rightAnchor - opts.edgeInset * opts.hudScale - scaledHalfW;
    // Float the strip's bottom edge ~14px above the frame's top edge.
    this.y = opts.frameTopY - scaledHalfH - 14 * opts.hudScale;
    this.layoutChildren();
  }

  // -------- internal layout helpers --------

  private get pendingBadgeRestY(): number {
    // Centre of the badge sits just below the pill (in widget-local
    // y, before this Container's own y/scale).
    return LoadoutStripHud.pillHeight / 2 + 18;
  }

  private layoutChildren(): void {
    const pillW = LoadoutStripHud.pillWidth;
    const pillH = LoadoutStripHud.pillHeight;
    // Tiles run left-to-right inside the pill, centred vertically.
    const firstTileLeft = -pillW / 2 + LoadoutStripHud.INNER_PAD_X;
    for (let i = 0; i < LoadoutStripHud.SLOTS; i++) {
      const tile = this.tileButtons[i];
      if (!tile) continue;
      const tileLeft =
        firstTileLeft +
        i * (LoadoutStripHud.TILE_SIZE + LoadoutStripHud.TILE_GAP);
      tile.x = tileLeft + LoadoutStripHud.TILE_SIZE / 2;
      tile.y = 0;
      tile.hitArea = new Rectangle(
        -LoadoutStripHud.TILE_SIZE / 2,
        -LoadoutStripHud.TILE_SIZE / 2,
        LoadoutStripHud.TILE_SIZE,
        LoadoutStripHud.TILE_SIZE,
      );
    }
    void pillH;
    this.refreshPendingBadge();
  }

  private drawBackground(): void {
    // Producer brief 2026-05-14: drop the gold pill chrome entirely
    // — the strip reads as 3 standalone tiles, not a surrounding
    // capsule. {@link drawTile} renders each tile's own opaque
    // navy fill below.
    this.background.clear();
  }

  private drawTiles(): void {
    for (let i = 0; i < LoadoutStripHud.SLOTS; i++) {
      this.drawTile(i, 0);
    }
  }

  private drawTile(i: number, pulsePhase: number): void {
    const bg = this.tileBackgrounds[i];
    if (!bg) return;
    const size = LoadoutStripHud.TILE_SIZE;
    const half = size / 2;
    const radius = 10;
    const hasItem = this.slotAliases[i] !== null;
    const isPending = this.pendingAlias !== null;
    bg.clear();
    // Opaque navy square + the same gold trim as the gear/info pill.
    // Producer brief 2026-05-15: "add the same gold trim around the
    // special items" — matches the cluster pill's recipe (thick
    // outer gold stroke + faint inner gold glow). When a pending
    // claim is up the outer border pulses brighter to signal "tap".
    bg.roundRect(-half, -half, size, size, radius)
      .fill({ color: 0x16113d, alpha: 1 });
    if (isPending) {
      // Pulsing gold border draws over the navy fill so all three
      // tiles read as valid tap targets during a claim.
      const borderAlpha = 0.7 + 0.3 * pulsePhase;
      bg.roundRect(-half, -half, size, size, radius)
        .stroke({ color: 0xffd86b, alpha: borderAlpha, width: 3 });
    } else {
      // Persistent gold trim on every tile (filled OR empty) so the
      // strip reads as a sibling of the gear/info cluster.
      bg.roundRect(-half, -half, size, size, radius)
        .stroke({ color: 0xffc940, alpha: 1, width: 3 });
    }
    // Inner soft-gold glow — mirrors the cluster pill's second
    // stroke for the polished "double-border" look.
    bg.roundRect(-half + 2, -half + 2, size - 4, size - 4, radius - 2)
      .stroke({ color: 0xffe28a, alpha: 0.35, width: 1 });
    void hasItem;
    // Empty + non-pending = no border (the opaque navy square
    // alone is enough to read as a placeholder slot).
  }

  private redrawTileBorders(pulsePhase: number): void {
    for (let i = 0; i < LoadoutStripHud.SLOTS; i++) {
      this.drawTile(i, pulsePhase);
    }
  }

  private refreshTiles(): void {
    this.redrawTileBorders(0);
    for (const t of this.tileButtons) {
      t.cursor = this.pendingAlias ? 'pointer' : 'default';
    }
  }

  private refreshTileIcons(): void {
    for (let i = 0; i < LoadoutStripHud.SLOTS; i++) {
      const alias = this.slotAliases[i];
      const existing = this.tileIcons[i];
      if (existing) {
        existing.destroy();
        this.tileIcons[i] = null;
      }
      if (!alias) continue;
      const tex = AssetLoader.shared.getTexture(alias);
      if (!tex) continue;
      this.tileIcons[i] = this.attachIcon(this.tileButtons[i]!, tex);
    }
    this.refreshTiles();
  }

  private attachIcon(parent: Container, tex: Texture): Sprite {
    // Icon nearly fills the tile (1px breathing room each side) so
    // the artwork reads clearly at HUD scale. Producer brief
    // 2026-05-14: "bigger still."
    const size = LoadoutStripHud.TILE_SIZE - 2;
    const sprite = new Sprite(tex);
    sprite.anchor.set(0.5);
    sprite.width = size;
    sprite.height = size;
    parent.addChild(sprite);
    return sprite;
  }

  private refreshPendingBadge(): void {
    if (!this.pendingAlias || !this.pendingItemName) {
      this.pendingBadge.visible = false;
      return;
    }
    this.pendingBadge.visible = true;
    const text = `TAP TO REPLACE ${this.pendingItemName.toUpperCase()}`;
    this.pendingBadgeText.text = text;
    this.pendingBadgeText.x = 0;
    this.pendingBadgeText.y = 0;
    // Draw a gold pill behind the text sized to its width.
    const w = this.pendingBadgeText.width + 22;
    const h = 26;
    const r = h / 2;
    this.pendingBadgeBg.clear();
    this.pendingBadgeBg
      .roundRect(-w / 2, -h / 2, w, h, r)
      .fill({ color: 0xffd86b, alpha: 0.95 })
      .roundRect(-w / 2, -h / 2, w, h, r)
      .stroke({ color: 0xffc940, alpha: 1, width: 2 });
    this.pendingBadge.x = 0;
    this.pendingBadge.y = this.pendingBadgeRestY;
  }
}
